/*
 * Telegram, through the Bot API: a bot posts to channels and groups it has been
 * added to (as an administrator, with "Post messages", for a channel).
 *
 * Read from the official docs, https://core.telegram.org/bots/api :
 *   - Making requests: https://api.telegram.org/bot<token>/METHOD_NAME. A
 *     response is JSON with `ok`; on failure `ok` is false and `description`
 *     explains it (`error_code` is "subject to change", so it is not used).
 *     Failures also arrive with a 4xx HTTP status, which is what the engine
 *     reads; `description` is therefore the message path.
 *   - getMe: the bot as a User (`result.id`, `result.username`).
 *   - getChat: `chat_id` is an Integer or "@channelusername"; returns
 *     ChatFullInfo (`result.id`, `result.title`, `result.username`).
 *   - sendMessage: `text` 1-4096 characters. sendPhoto / sendVideo: `caption`
 *     0-1024 characters. Returns the Message (`result.message_id`).
 *   - Sending files: a multipart/form-data upload is limited to 10 MB for a
 *     photo and 50 MB for other files (https://core.telegram.org/bots/api#sending-files).
 *     sendVideo: "Bots can currently send video files of up to 50 MB".
 *   - editMessageText edits text messages (1-4096 characters); editMessageCaption
 *     edits the caption of a media message. deleteMessage: only a message sent
 *     less than 48 hours ago, and a bot needs the "Post messages" right to delete
 *     its own posts in a channel.
 *
 * How the contract is shaped, and what it gives up:
 *   - The bot token is pasted as the app's secret and rides in the path
 *     (`/bot<token>/method`), never in a header. The bot is the "identity";
 *     the channels are named by the person and each is looked up with getChat,
 *     so a typo or a channel the bot cannot see fails at connection time.
 *   - The stored account id is the numeric chat id getChat answers with (for a
 *     channel, `-100...`), whatever the person typed. A numeric id works for
 *     public and private chats alike and survives a channel being renamed.
 *   - maxTextLength is 1024: the contract has one limit for every kind, and
 *     1024 is the caption limit of photos and videos. A text-only post could
 *     be 4096; it is held to 1024 so that no post is accepted here and then
 *     refused by Telegram. Raise it to 4096 for a text-only deployment.
 *   - No parse_mode is sent: the text is delivered as typed, so a stray `_` or
 *     `<` can never make Telegram reject a post.
 *   - A LINK post is the text followed by the link; Telegram unfurls it.
 *   - MULTI_PHOTO (sendMediaGroup) is left out. It needs one `media` JSON array
 *     naming each file as `attach://<field>` plus one multipart field per file;
 *     a request here can carry exactly one file, in one named field.
 *   - There is no way for a bot to list a chat's past posts, so no `list`.
 *   - Edit uses editMessageText, so it works for TEXT and LINK posts. A PHOTO or
 *     VIDEO post needs editMessageCaption, a different method, and an edit is
 *     one request whatever the post's kind, so editing a media post is refused
 *     by Telegram ("there is no text in the message to edit"). Editing a LINK
 *     post replaces the whole text, so the link is dropped unless it is in the
 *     new text.
 *
 * Uncertain, to confirm with a live bot (verifiedLive stays false):
 *   - Permalink. A message in a public chat is https://t.me/<username>/<id>; in
 *     a private one https://t.me/c/<internal id>/<id>, where the internal id is
 *     the chat id without its `-100` prefix. Both need something the contract
 *     cannot produce from a stored `-100...` id (the username, or the id with
 *     the prefix cut). The public form is used below, which is only right if
 *     the stored id is "@username"; with the `-100...` id getChat answers
 *     with it is NOT a working link. The engine change that fixes it is to let
 *     accountLookup store the id as the person typed it (an `accountId`
 *     template, `{{entry.id}}`), plus a way to cut `-100` for private chats.
 *   - The 429 flood-control wait is in the body (`parameters.retry_after`);
 *     the engine reads the Retry-After header, so it falls back to its own backoff.
 *   - A chat id and message id are sent as strings in JSON (and as form text on
 *     edit and delete); the Bot API reads every argument as text, so this is
 *     expected to be accepted.
 */
const API = 'https://api.telegram.org';
export const TELEGRAM_PRESET = {
    key: 'TELEGRAM',
    name: 'Telegram',
    verifiedLive: false,
    contract: {
        version: 1,
        allowedHosts: ['api.telegram.org'],
        auth: {
            type: 'token',
            clientIdLabel: 'Bot username',
            clientSecretLabel: 'Bot token (from @BotFather)',
            accountsLabel: 'Channels or groups (@username or numeric id)',
            accountLookup: {
                request: { method: 'GET', url: `${API}/getChat?chat_id={{entry.id}}` },
                idPath: 'result.id',
                namePath: 'result.title',
                // Kept as typed: an @username then gives a working public link, and a
                // numeric id still posts (its link cannot be built: t.me/c/ needs the
                // id without its -100 prefix).
                accountId: '{{entry.id}}',
                kind: 'ACCOUNT',
            },
        },
        tokenPlacement: { in: 'path-prefix', prefix: 'bot' },
        identity: {
            request: { method: 'GET', url: `${API}/getMe` },
            idPath: 'result.id',
            namePath: 'result.username',
            accountId: '{{entry.id}}',
            kind: 'ACCOUNT',
        },
        publish: {
            kinds: ['TEXT', 'LINK', 'PHOTO', 'VIDEO'],
            request: {
                method: 'POST',
                url: `${API}/sendMessage`,
                json: { chat_id: '{{account.id}}', text: '{{text}}' },
            },
            perKind: {
                LINK: { text: '{{text}}\n\n{{link}}' },
            },
            requests: {
                PHOTO: {
                    request: {
                        method: 'POST',
                        url: `${API}/sendPhoto`,
                        multipart: {
                            fields: { chat_id: '{{account.id}}', caption: '{{text}}' },
                            fileField: 'photo',
                        },
                    },
                },
                VIDEO: {
                    request: {
                        method: 'POST',
                        url: `${API}/sendVideo`,
                        multipart: {
                            fields: {
                                chat_id: '{{account.id}}',
                                caption: '{{text}}',
                                supports_streaming: 'true',
                            },
                            fileField: 'video',
                        },
                    },
                },
            },
            resultId: { from: 'body', path: 'result.message_id' },
            maxTextLength: 1024,
        },
        posts: {
            edit: {
                method: 'POST',
                url: `${API}/editMessageText`,
                form: { chat_id: '{{account.id}}', message_id: '{{postId}}', text: '{{text}}' },
            },
            delete: {
                method: 'POST',
                url: `${API}/deleteMessage`,
                form: { chat_id: '{{account.id}}', message_id: '{{postId}}' },
            },
        },
        permalink: 'https://t.me/{{account.handle}}/{{postId}}',
        errorMessagePaths: ['description'],
    },
};
//# sourceMappingURL=telegram.js.map